import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Radio, RefreshCw, Loader2, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  useNotificationChannelsQuery,
  useNotificationProvidersQuery,
  useProvidersHealthQuery,
} from '@/client/api/notifications-infra/notifications.query';
import { ProviderHealth } from '@/client/types/notifications-infra';
import {
  CapabilityChips,
  DELIVERY_BASE,
  DeliveryAccessDenied,
  DeliveryPageShell,
  HealthStatus,
  formatChannel,
  formatProviderName,
  providerKey,
  resolveHealth,
  routedCountryCount,
} from '@/client/components/admin/NotificationsInfra/deliveryUi';

const DeliveryProvidersPage = () => {
  useBreadcrumbs([
    { label: 'Communications', href: `${DELIVERY_BASE}/delivery` },
    { label: 'Notifications' },
    { label: 'Providers' },
  ]);

  const viewPerm = useHasPermission('messaging.view');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const [channel, setChannel] = useState<string>('');
  const channelsQ = useNotificationChannelsQuery(canView);
  const providersQ = useNotificationProvidersQuery(channel || undefined, canView);
  const healthQ = useProvidersHealthQuery(undefined, canView);

  const providers = providersQ.data?.providers ?? [];
  const channels = useMemo(() => {
    const fromApi = (channelsQ.data?.channels ?? []).map((c) => String(c.channel));
    const fromProviders = providers.map((p) => (p.channel ? String(p.channel) : '')).filter(Boolean);
    return Array.from(new Set([...fromApi, ...fromProviders]));
  }, [channelsQ.data, providers]);

  const healthByProvider = new Map<string, ProviderHealth>();
  for (const h of healthQ.data?.providers ?? []) {
    const key = providerKey(h);
    if (key) healthByProvider.set(key, h);
  }
  const proxyUnavailable = !!healthQ.data?.unavailable;

  if (permLoading) {
    return (
      <DeliveryPageShell loading icon={null} title="Providers" description="">
        {null}
      </DeliveryPageShell>
    );
  }

  if (!canView) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <DeliveryAccessDenied />
        </div>
      </div>
    );
  }

  return (
    <DeliveryPageShell
      icon={<Radio className="h-4 w-4" />}
      title="Providers"
      description="Registry rows and adapters. Affordances come from capabilities — adding Mailgun later needs no UI change."
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            providersQ.refetch();
            healthQ.refetch();
          }}
          disabled={providersQ.isFetching || healthQ.isFetching}
          className="gap-2"
        >
          {providersQ.isFetching || healthQ.isFetching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Refresh
        </Button>
      }
    >
      {proxyUnavailable && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          Live vendor health is unavailable (our proxy), not a vendor outage.
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setChannel('')}
          className={`rounded-md border px-3 py-1.5 text-sm transition-colors duration-150 ${
            channel === ''
              ? 'border-blue-200 bg-blue-50 text-blue-800'
              : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          All channels
        </button>
        {channels.map((ch) => (
          <button
            key={ch}
            type="button"
            onClick={() => setChannel(ch)}
            className={`rounded-md border px-3 py-1.5 text-sm transition-colors duration-150 ${
              channel === ch
                ? 'border-blue-200 bg-blue-50 text-blue-800'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {formatChannel(ch)}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        {providersQ.isLoading ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : providersQ.isError ? (
          <p className="p-8 text-center text-sm text-red-600">
            {getApiErrorMessage(providersQ.error, 'Failed to load providers.')}
          </p>
        ) : providers.length === 0 ? (
          <p className="p-8 text-center text-sm text-gray-500">No providers for this channel.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {providers.map((p) => {
              const key = providerKey(p);
              const count = routedCountryCount(p);
              return (
                <li key={key}>
                  <Link
                    to={`${DELIVERY_BASE}/providers/${encodeURIComponent(key)}`}
                    className="flex items-center gap-4 px-4 py-4 transition-colors duration-150 hover:bg-gray-50"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-gray-900">{formatProviderName(key)}</p>
                        {p.is_default ? (
                          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                            Default
                          </span>
                        ) : null}
                        {p.channel ? (
                          <span className="text-xs text-gray-500">{formatChannel(String(p.channel))}</span>
                        ) : null}
                      </div>
                      <div className="mt-2">
                        <CapabilityChips caps={p.capabilities ?? []} />
                      </div>
                      {count != null && String(p.channel) === 'sms' ? (
                        <p className="mt-1 text-xs text-gray-500">
                          {count === 0
                            ? 'Registered but carries no SMS traffic'
                            : `Routes ${count} ${count === 1 ? 'country' : 'countries'}`}
                        </p>
                      ) : null}
                    </div>
                    <div className="shrink-0 text-right">
                      <HealthStatus
                        state={resolveHealth(
                          p,
                          healthByProvider.get(key),
                          proxyUnavailable,
                          healthQ.isLoading,
                        )}
                      />
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </DeliveryPageShell>
  );
};

export default DeliveryProvidersPage;
